import { createRoot } from 'react-dom/client'
import { HelmetProvider } from 'react-helmet-async'
import App from './App.tsx'
import './index.css'
import { installSafeSonner } from './lib/safeSonner'

installSafeSonner();

const path = window.location.pathname;
const isAdminRoute = path.startsWith('/admin');
const isHrRoute = path.startsWith('/hr');

// Capture beforeinstallprompt globally and EARLY (before React mounts).
// Chromium fires this once, very early — if we wait for AdminLogin's
// useEffect to attach a listener, the event is already gone on cold loads.
// We stash it on window so AdminLogin can hydrate from it on mount.
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  (window as any).__hibaneAdminInstallPrompt = e;
  window.dispatchEvent(new Event('hibane-install-available'));
});
window.addEventListener('appinstalled', () => {
  (window as any).__hibaneAdminInstallPrompt = null;
});

// hibane.ai (marketing) is NOT a PWA. Only /admin/* (HIBANE Desk) installs a SW
// from AdminLogin.tsx with scope '/admin/'. We aggressively clean up any legacy
// service worker that previously held scope '/' (or scope '/admin/' under the
// wrong script URL) so it cannot block Chrome's beforeinstallprompt for the
// admin PWA. See .hibane/contracts/marketing-no-pwa.md.
if ('serviceWorker' in navigator) {
  if (import.meta.env.PROD) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.getRegistrations().then((regs) => {
        regs.forEach((reg) => {
          try {
            const scopeUrl = new URL(reg.scope);
            const swUrl =
              reg.active?.scriptURL ||
              reg.installing?.scriptURL ||
              reg.waiting?.scriptURL ||
              '';
            const scopePath = scopeUrl.pathname;

            // Anything at the root scope must go — marketing is not a PWA.
            if (scopePath === '/') {
              reg.unregister().catch(() => {});
              return;
            }

            // Legacy: admin SW that was once registered at root scope.
            if (swUrl.endsWith('/admin-sw.js') && scopePath === '/') {
              reg.unregister().catch(() => {});
              return;
            }

            // Don't touch valid /admin/, /hr/, or /voice/ scoped registrations —
            // those are the standalone Desk PWAs and have their own owners.
          } catch { /* noop */ }
        });
      }).catch(() => {});
    });
  } else if (import.meta.env.DEV) {
    // Unregister all SWs in development to prevent caching issues.
    navigator.serviceWorker.getRegistrations().then((regs) => {
      regs.forEach((r) => r.unregister());
    });
  }

  // Early SW registration for /voice/* so users who land directly on a voice
  // route (not via /voice/login) still trigger Chromium's beforeinstallprompt.
  // Mirrors the /admin pattern in AdminLogin.tsx. Skip in iframes (preview).
  if (import.meta.env.PROD && path.startsWith('/voice')) {
    const inIframe = (() => { try { return window.self !== window.top; } catch { return true; } })();
    if (!inIframe) {
      import('@/lib/pwa/swUpdater').then(({ registerAndAutoUpdate }) => {
        registerAndAutoUpdate('/voice-sw.js', { scope: '/voice/' }).catch(() => {});
      }).catch(() => {});
    }
  }

  // Early SW registration for /socials/* (Phase 1 of standalone Social Studio).
  if (import.meta.env.PROD && path.startsWith('/socials')) {
    const inIframe = (() => { try { return window.self !== window.top; } catch { return true; } })();
    if (!inIframe) {
      import('@/lib/pwa/swUpdater').then(({ registerAndAutoUpdate }) => {
        registerAndAutoUpdate('/socials-sw.js', { scope: '/socials/' }).catch(() => {});
      }).catch(() => {});
    }
  }
}

// Touch the admin/hr route flags so tree-shaking keeps them — they are read
// elsewhere in the app to gate UI.
void isAdminRoute;
void isHrRoute;

createRoot(document.getElementById("root")!).render(
  <HelmetProvider>
    <App />
  </HelmetProvider>
);
